import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ENVIRONMENT_TOKEN } from '@aksia/infrastructure';
import { ToasterService } from '@aksia/ui';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  AccountSecurityStatus,
  SECOND_FACTOR_LABELS,
  SecondFactorMethod,
  SecondFactorService,
} from '../../services/second-factor.service';
import { isValidReturnUrl } from '../../utils/return-url.utils';
import { QrCodeComponent } from '../../components/qr-code/qr-code.component';

@Component({
  selector: 'app-security',
  imports: [QrCodeComponent],
  templateUrl: './security.content.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecurityPageContent implements OnInit {
  private readonly toaster = inject(ToasterService);
  private readonly secondFactor = inject(SecondFactorService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly environment = inject(ENVIRONMENT_TOKEN);

  protected readonly returnUrl = (() => {
    const url = inject(ActivatedRoute).snapshot.queryParamMap.get('returnUrl');
    return isValidReturnUrl(url) ? url : null;
  })();
  protected readonly labels = SECOND_FACTOR_LABELS;

  protected readonly status = signal<AccountSecurityStatus | null>(null);
  protected readonly signedOut = signal(false);
  protected readonly inFlight = signal(false);

  protected readonly password = signal('');
  protected readonly authenticatorKey = signal<string | null>(null);
  protected readonly authenticatorLink = signal<SafeUrl | null>(null);
  protected readonly authenticatorUri = signal<string | null>(null);
  protected readonly authenticatorCode = signal('');
  protected readonly recoveryCodes = signal<string[] | null>(null);
  protected readonly phoneNumber = signal('');
  protected readonly phoneCodeSent = signal(false);
  protected readonly phoneCode = signal('');

  @HostBinding('class')
  protected readonly hostClass = 'auth-page';

  protected continueToApp() {
    window.location.href =
      this.environment.microservices_api_url + this.returnUrl;
  }

  ngOnInit() {
    this.load();
  }

  protected load() {
    this.secondFactor.getSecurity().subscribe({
      next: (status) => this.status.set(status),
      error: (err: HttpErrorResponse) => this.handleError(err),
    });
  }

  protected startAuthenticator() {
    this.run(
      this.secondFactor.startAuthenticatorSetup(this.password()),
      (setup) => {
        this.authenticatorKey.set(setup.sharedKey);
        this.authenticatorLink.set(
          this.sanitizer.bypassSecurityTrustUrl(setup.authenticatorUri),
        );
        this.authenticatorUri.set(setup.authenticatorUri);
      },
    );
  }

  protected finishAuthenticator() {
    this.run(
      this.secondFactor.finishAuthenticatorSetup(this.authenticatorCode()),
      (res) => {
        this.authenticatorKey.set(null);
        this.authenticatorCode.set('');
        this.recoveryCodes.set(res.recoveryCodes);
        this.load();
      },
    );
  }

  protected removeAuthenticator() {
    this.run(this.secondFactor.removeAuthenticator(this.password()), () => {
      this.toaster.success('Authenticator removed.');
      this.load();
    });
  }

  protected regenerateRecoveryCodes() {
    this.run(
      this.secondFactor.regenerateRecoveryCodes(this.password()),
      (res) => {
        this.recoveryCodes.set(res.recoveryCodes);
        this.load();
      },
    );
  }

  protected startPhone() {
    this.run(
      this.secondFactor.startPhoneChange(
        this.password(),
        this.phoneNumber().trim(),
      ),
      () => {
        this.phoneCodeSent.set(true);
      },
    );
  }

  protected confirmPhone() {
    this.run(
      this.secondFactor.confirmPhoneChange(
        this.phoneNumber().trim(),
        this.phoneCode().trim(),
      ),
      () => {
        this.phoneCodeSent.set(false);
        this.phoneCode.set('');
        this.toaster.success('Phone number saved.');
        this.load();
      },
    );
  }

  protected removePhone() {
    this.run(this.secondFactor.removePhone(this.password()), () => {
      this.toaster.success('Phone number removed.');
      this.load();
    });
  }

  protected setPreferred(method: SecondFactorMethod) {
    this.run(this.secondFactor.setPreferredMethod(method), () => this.load());
  }

  protected value(event: Event) {
    return (event.target as HTMLInputElement).value;
  }

  private run<T>(call: Observable<T>, onSuccess: (result: T) => void) {
    this.inFlight.set(true);
    call.subscribe({
      next: (result) => {
        this.inFlight.set(false);
        onSuccess(result);
      },
      error: (err: HttpErrorResponse) => {
        this.inFlight.set(false);
        this.handleError(err);
      },
    });
  }

  private handleError(err: HttpErrorResponse) {
    if (err.status === 401) {
      this.signedOut.set(true);
      return;
    }

    if (err.status === 429) {
      this.toaster.error(
        'Too many attempts. Please wait a moment and try again.',
      );
      return;
    }

    this.toaster.error(
      (err.error?.error_description as string | undefined) ??
        'Something went wrong. Please try again.',
    );
  }
}
